import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Phone, Trash2, Plus, Send, Bell, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const WEEKDAY_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
const HOURS = Array.from({ length: 6 }, (_, i) => 12 + i); // 12..17

const asText = (x) => typeof x === "string" ? x : (Array.isArray(x) ? x.map(i => i?.msg || JSON.stringify(i)).join("; ") : (x?.message || x?.detail || JSON.stringify(x || {})));

// Format ISO/UTC timestamp as Berlin local time "DD.MM.YYYY HH:MM"
const formatBerlin = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 16).replace("T", " ");
    const p = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(d);
    return p.replace(",", "");
  } catch {
    return String(iso).slice(0, 16).replace("T", " ");
  }
};

export default function AdminVoiceAdvisors({ authToken }) {
  const auth = { headers: { Authorization: `Bearer ${authToken}` } };
  const [advisors, setAdvisors] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [smsLog, setSmsLog] = useState([]);
  const [testPhone, setTestPhone] = useState("");
  const [testBody, setTestBody] = useState("");
  const [testing, setTesting] = useState(false);
  const [current, setCurrent] = useState(null);

  const [newAdvisorName, setNewAdvisorName] = useState("");
  const [newAdvisorPhone, setNewAdvisorPhone] = useState("");
  const [newShiftWd, setNewShiftWd] = useState(0);
  const [newShiftFrom, setNewShiftFrom] = useState("12:00");
  const [newShiftTo, setNewShiftTo] = useState("15:00");
  const [newShiftAdvisor, setNewShiftAdvisor] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, s, se, l, cu] = await Promise.all([
        axios.get(`${API}/admin/voice-advisors`, auth),
        axios.get(`${API}/admin/voice-advisor-shifts`, auth),
        axios.get(`${API}/admin/sms-settings`, auth),
        axios.get(`${API}/admin/sms-log?limit=30`, auth),
        axios.get(`${API}/admin/voice-advisor-shifts/current`, auth),
      ]);
      setAdvisors(a.data.advisors || []);
      setShifts(s.data.shifts || []);
      setSettings(se.data || {});
      setSmsLog(l.data.log || []);
      setCurrent(cu.data || null);
    } catch { toast.error("Konnte Beraterinnen-Daten nicht laden"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  const addAdvisor = async () => {
    if (!newAdvisorName.trim() || !newAdvisorPhone.trim()) return toast.error("Name + Nummer erforderlich");
    try {
      await axios.post(`${API}/admin/voice-advisors`, { name: newAdvisorName.trim(), phone: newAdvisorPhone.trim(), active: true }, auth);
      setNewAdvisorName(""); setNewAdvisorPhone("");
      toast.success("Beraterin hinzugefügt");
      load();
    } catch { toast.error("Fehler beim Hinzufügen"); }
  };

  const updateAdvisor = async (id, changes) => {
    try { await axios.put(`${API}/admin/voice-advisors/${id}`, changes, auth); load(); }
    catch { toast.error("Update fehlgeschlagen"); }
  };

  const removeAdvisor = async (id, name) => {
    if (!window.confirm(`Beraterin "${name}" wirklich löschen? Zugehörige Schichten werden auch entfernt.`)) return;
    try { await axios.delete(`${API}/admin/voice-advisors/${id}`, auth); toast.success("Gelöscht"); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const addShift = async () => {
    if (!newShiftAdvisor) return toast.error("Beraterin auswählen");
    try {
      await axios.post(`${API}/admin/voice-advisor-shifts`, {
        weekday: newShiftWd, from_time: newShiftFrom, to_time: newShiftTo,
        advisor_id: newShiftAdvisor, active: true,
      }, auth);
      toast.success(`Schicht gespeichert & aktiv: ${WEEKDAY_LONG[newShiftWd]} ${newShiftFrom}–${newShiftTo}`);
      load();
    } catch { toast.error("Schicht anlegen fehlgeschlagen"); }
  };

  const removeShift = async (id) => {
    try { await axios.delete(`${API}/admin/voice-advisor-shifts/${id}`, auth); load(); }
    catch { toast.error("Schicht löschen fehlgeschlagen"); }
  };

  const saveSettings = async (changes) => {
    try {
      const r = await axios.put(`${API}/admin/sms-settings`, changes, auth);
      setSettings(r.data);
      toast.success("Gespeichert");
    } catch { toast.error("Speichern fehlgeschlagen"); }
  };

  const sendTestSms = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/sms-test`, {
        to_phone: testPhone || undefined, body: testBody || undefined,
      }, auth);
      if (r.data?.result?.ok) toast.success("Test-SMS versendet ✓");
      else toast.error(`Fehler: ${asText(r.data?.result?.error).slice(0, 200) || "?"}`);
      load();
    } catch (e) { toast.error(asText(e?.response?.data?.detail || "Test-SMS fehlgeschlagen")); }
    setTesting(false);
  };

  const deleteSmsLogEntry = async (id) => {
    if (!window.confirm("Diesen SMS-Log-Eintrag löschen?")) return;
    try {
      await axios.delete(`${API}/admin/sms-log/${id}`, auth);
      toast.success("Eintrag gelöscht");
      load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const bulkDeleteSmsLog = async (count) => {
    const label = count === "all" ? "ALLE SMS-Log-Einträge" : `die ältesten ${count} Einträge`;
    if (!window.confirm(`${label} wirklich löschen?`)) return;
    try {
      const r = await axios.post(`${API}/admin/sms-log/bulk-delete`, { count }, auth);
      toast.success(`${r.data?.deleted || 0} Einträge gelöscht`);
      load();
    } catch { toast.error("Bulk-Löschen fehlgeschlagen"); }
  };

  const advisorById = (id) => advisors.find(a => a.id === id);
  const matrixCell = (wd, hour) => {
    for (const s of shifts) {
      if (s.weekday !== wd || !s.active) continue;
      const fh = parseInt(s.from_time?.split(":")[0] || "0", 10);
      const th = parseInt(s.to_time?.split(":")[0] || "0", 10);
      if (hour >= fh && hour < th) return s;
    }
    return null;
  };

  return (
    <div className="space-y-4" data-testid="voice-advisor-panel">
      <div className="rounded-xl border bg-blue-50 border-blue-200 p-3 flex items-center gap-3" data-testid="current-shift-info">
        <Phone className="h-5 w-5 text-blue-600" />
        <div className="flex-1">
          <div className="text-[11px] text-gray-600">Aktuell im Dienst</div>
          <div className="text-sm font-semibold text-gray-800">
            {current?.current_advisor
              ? `${current.current_advisor.name} — ${current.current_advisor.phone}`
              : "Niemand im Dienst → SMS geht an Fallback"}
          </div>
          <div className="text-[10px] text-gray-500">{WEEKDAY_LONG[current?.weekday ?? 0]} • Berlin-Zeit</div>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-3" data-testid="advisor-list">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">👥 Beraterinnen ({advisors.length})</div>
          <div className="text-[10px] text-gray-500 italic">Änderungen an Name/Nummer werden automatisch beim Klicken außerhalb gespeichert</div>
        </div>
        <div className="space-y-1.5">
          {advisors.map((a) => (
            <div key={a.id} className="flex items-center gap-2 border rounded-lg p-1.5" data-testid={`advisor-row-${a.id}`}>
              <Input value={a.name}
                onChange={(e) => setAdvisors(advisors.map(x => x.id === a.id ? { ...x, name: e.target.value } : x))}
                onBlur={() => updateAdvisor(a.id, { name: a.name })}
                className="text-sm flex-1 h-8" data-testid={`advisor-name-${a.id}`} />
              <Input value={a.phone}
                onChange={(e) => setAdvisors(advisors.map(x => x.id === a.id ? { ...x, phone: e.target.value } : x))}
                onBlur={() => updateAdvisor(a.id, { phone: a.phone })}
                placeholder="+49..." className="text-sm flex-1 h-8" data-testid={`advisor-phone-${a.id}`} />
              <div className="flex items-center gap-1 text-[11px]">
                <Switch checked={!!a.active}
                  onCheckedChange={(v) => updateAdvisor(a.id, { active: v })}
                  data-testid={`advisor-active-${a.id}`} />
                <span className="text-gray-500">Aktiv</span>
              </div>
              <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                onClick={() => removeAdvisor(a.id, a.name)} data-testid={`advisor-delete-${a.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2 border-t pt-2">
          <Input placeholder="Name" value={newAdvisorName} onChange={(e) => setNewAdvisorName(e.target.value)}
            className="text-sm flex-1 h-8" data-testid="new-advisor-name" />
          <Input placeholder="+49..." value={newAdvisorPhone} onChange={(e) => setNewAdvisorPhone(e.target.value)}
            className="text-sm flex-1 h-8" data-testid="new-advisor-phone" />
          <Button size="sm" onClick={addAdvisor} data-testid="add-advisor-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8">
            <Plus className="h-3.5 w-3.5 mr-1" /> Hinzu
          </Button>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-3" data-testid="shift-matrix">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">🗓️ Schichtplan (Mo–So × 12–17 Uhr)</div>
          <div className="text-[10px] text-gray-500">Änderungen unten sofort speichern → hier sichtbar</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="p-1.5 text-left w-14">Zeit</th>
                {WEEKDAYS.map((d, i) => <th key={i} className="p-1.5 text-center border-l">{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {HOURS.map((h) => (
                <tr key={h} className="border-b">
                  <td className="p-1.5 font-mono text-gray-500">{h}–{h+1}</td>
                  {WEEKDAYS.map((_, wd) => {
                    const s = matrixCell(wd, h);
                    const adv = s ? advisorById(s.advisor_id) : null;
                    const idx = adv ? advisors.findIndex(x => x.id === adv.id) : -1;
                    const colors = ["bg-blue-100 text-blue-800","bg-emerald-100 text-emerald-800","bg-purple-100 text-purple-800","bg-orange-100 text-orange-800","bg-pink-100 text-pink-800"];
                    const cls = adv ? colors[idx % 5] : "bg-gray-50 text-gray-400";
                    return <td key={wd} className={`p-1.5 text-center border-l ${cls}`}>{adv?.name || "—"}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-3" data-testid="shift-list">
        <div className="text-sm font-semibold mb-2">Schichten bearbeiten ({shifts.length})</div>
        <div className="space-y-1">
          {shifts.map((s) => {
            const adv = advisorById(s.advisor_id);
            return (
              <div key={s.id} className="flex items-center gap-2 border rounded p-1.5 text-xs" data-testid={`shift-row-${s.id}`}>
                <span className="w-16 font-semibold">{WEEKDAY_LONG[s.weekday]}</span>
                <span className="font-mono">{s.from_time}–{s.to_time}</span>
                <span className="flex-1">→ {adv?.name || "?"}</span>
                <Switch checked={!!s.active}
                  onCheckedChange={(v) => axios.put(`${API}/admin/voice-advisor-shifts/${s.id}`, { active: v }, auth).then(load)}
                  data-testid={`shift-active-${s.id}`} />
                <Button size="sm" variant="ghost" onClick={() => removeShift(s.id)}
                  className="text-red-600 h-7 w-7 p-0" data-testid={`shift-delete-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
          {shifts.length === 0 && <div className="text-xs text-gray-500 italic p-2">Noch keine Schichten angelegt.</div>}
        </div>
        <div className="mt-2 pt-2 border-t grid grid-cols-5 gap-2 items-end">
          <div>
            <div className="text-[10px] uppercase text-gray-500 mb-1">Tag</div>
            <select value={newShiftWd} onChange={(e) => setNewShiftWd(parseInt(e.target.value, 10))}
              className="text-xs border rounded px-2 py-1.5 w-full h-8" data-testid="new-shift-wd">
              {WEEKDAY_LONG.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase text-gray-500 mb-1">Von</div>
            <Input type="time" value={newShiftFrom} onChange={(e) => setNewShiftFrom(e.target.value)} className="text-xs h-8" data-testid="new-shift-from" />
          </div>
          <div>
            <div className="text-[10px] uppercase text-gray-500 mb-1">Bis</div>
            <Input type="time" value={newShiftTo} onChange={(e) => setNewShiftTo(e.target.value)} className="text-xs h-8" data-testid="new-shift-to" />
          </div>
          <div>
            <div className="text-[10px] uppercase text-gray-500 mb-1">Beraterin</div>
            <select value={newShiftAdvisor} onChange={(e) => setNewShiftAdvisor(e.target.value)}
              className="text-xs border rounded px-2 py-1.5 w-full h-8" data-testid="new-shift-advisor">
              <option value="">Wählen...</option>
              {advisors.filter(a => a.active).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <Button size="sm" onClick={addShift} data-testid="add-shift-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8">
            <Plus className="h-3.5 w-3.5 mr-1" /> Speichern
          </Button>
        </div>
      </div>

      {settings && (
        <div className="rounded-xl border bg-white p-3" data-testid="sms-settings">
          <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Bell className="h-4 w-4" /> SMS-Benachrichtigungen</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!settings.enabled}
                onCheckedChange={(v) => saveSettings({ enabled: v })}
                data-testid="sms-enabled-switch" />
              <span className={settings.enabled ? "text-emerald-700 font-semibold" : "text-gray-500"}>
                {settings.enabled ? "SMS AN" : "SMS AUS"}
              </span>
            </label>
            <div>
              <div className="text-[10px] uppercase text-gray-500 mb-1">Fallback-Nummer</div>
              <Input value={settings.fallback_phone || ""}
                onChange={(e) => setSettings({ ...settings, fallback_phone: e.target.value })}
                onBlur={() => saveSettings({ fallback_phone: settings.fallback_phone })}
                placeholder="+49..." className="text-sm h-8" data-testid="fallback-phone" />
            </div>
            <div>
              <div className="text-[10px] uppercase text-gray-500 mb-1">Sender-ID (max 11 Zeichen)</div>
              <Input value={settings.sender_id || ""}
                onChange={(e) => setSettings({ ...settings, sender_id: e.target.value })}
                onBlur={() => saveSettings({ sender_id: settings.sender_id })}
                placeholder="HypeFactory" maxLength={11} className="text-sm h-8" data-testid="sender-id" />
            </div>
            <div>
              <div className="text-[10px] uppercase text-gray-500 mb-1">Test-SMS-Nummer</div>
              <Input value={settings.sms_test_phone || ""}
                onChange={(e) => setSettings({ ...settings, sms_test_phone: e.target.value })}
                onBlur={() => saveSettings({ sms_test_phone: settings.sms_test_phone })}
                placeholder="+49..." className="text-sm h-8" data-testid="test-phone-setting" />
            </div>
          </div>
          <div className="mt-2 pt-2 border-t">
            <div className="text-[10px] uppercase text-gray-500 mb-2">Auslöser</div>
            <div className="flex flex-wrap gap-3 text-xs">
              {[
                { key: "callback", label: "Beraterin-Rückruf-Wunsch" },
                { key: "interested", label: "Interessent will verbunden werden" },
                { key: "explicit_name", label: "Anrufer nennt Namen (Nicole/Jana)" },
              ].map(t => (
                <label key={t.key} className="flex items-center gap-2">
                  <Switch checked={!!settings.triggers?.[t.key]}
                    onCheckedChange={(v) => saveSettings({ triggers: { ...settings.triggers, [t.key]: v } })}
                    data-testid={`trigger-${t.key}`} />
                  <span>{t.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-white p-3" data-testid="test-sms-box">
        <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Send className="h-4 w-4" /> Test-SMS senden</div>
        <div className="grid grid-cols-3 gap-2">
          <Input placeholder="Empfänger (leer = Test-Nummer)" value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className="text-sm col-span-1 h-8" data-testid="test-sms-phone" />
          <Textarea placeholder="Text (leer = Default)" value={testBody}
            onChange={(e) => setTestBody(e.target.value)}
            className="text-sm col-span-2 h-10 min-h-8" data-testid="test-sms-body" />
        </div>
        <Button onClick={sendTestSms} disabled={testing} className="mt-2 bg-blue-600 hover:bg-blue-700 text-white h-8 text-sm"
          data-testid="send-test-sms-btn">
          {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
          Test-SMS jetzt senden
        </Button>
      </div>

      <div className="rounded-xl border bg-white p-3" data-testid="sms-log">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="text-sm font-semibold">📋 SMS-Log (letzte 30)</div>
          {smsLog.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[10px] text-gray-500 mr-1">Ältere löschen:</span>
              <Button
                onClick={() => bulkDeleteSmsLog(10)}
                variant="outline"
                className="h-7 text-[11px] px-2"
                data-testid="sms-log-delete-10"
              >10</Button>
              <Button
                onClick={() => bulkDeleteSmsLog(20)}
                variant="outline"
                className="h-7 text-[11px] px-2"
                data-testid="sms-log-delete-20"
              >20</Button>
              <Button
                onClick={() => bulkDeleteSmsLog(50)}
                variant="outline"
                className="h-7 text-[11px] px-2"
                data-testid="sms-log-delete-50"
              >50</Button>
              <Button
                onClick={() => bulkDeleteSmsLog("all")}
                variant="outline"
                className="h-7 text-[11px] px-2 text-red-600 hover:bg-red-50 border-red-300"
                data-testid="sms-log-delete-all"
              >Alle</Button>
            </div>
          )}
        </div>
        {smsLog.length === 0
          ? <div className="text-xs text-gray-500 italic">Noch keine SMS versendet.</div>
          : <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {smsLog.map((s) => (
                <div key={s.id} className="border rounded p-2 text-xs flex items-start gap-2">
                  {s.status === "sent"
                    ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                    : <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <div className="font-mono text-[10px] text-gray-500">{formatBerlin(s.created_at)}</div>
                    <div className="text-gray-800">→ <b>{s.to_name}</b> ({s.to_phone})
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{s.match_rule}</span>
                    </div>
                    <div className="text-gray-600 whitespace-pre-wrap text-[11px]">{asText(s.body)}</div>
                    {s.error && <div className="text-red-600 text-[10px] mt-1">⚠️ {asText(s.error)}</div>}
                  </div>
                  <button
                    onClick={() => deleteSmsLogEntry(s.id)}
                    className="text-gray-400 hover:text-red-600 shrink-0 mt-0.5"
                    title="Diesen Eintrag löschen"
                    data-testid={`sms-log-delete-${s.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
        }
      </div>
    </div>
  );
}
